import * as Sentry from "@sentry/react";

import {
  AKA_FAMILY_ACCIDENT_ABOUT_INSURANCE_STEP,
  AKA_FAMILY_ACCIDENT_PRICE_STEP,
} from "../akaFamilyAccidentSteps";

import { AkaRenewalInfoAlert } from "Components/AkaRenewalInfoAlert";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { CheckPriceProps } from "Components/Features/CheckPrice/checkPriceConstants";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { NavigationButtons } from "Components/NavigationButtons";
import { useTexts } from "Components/TextContext";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { AkaFamilyAccidentCoverage } from "Store/interfaces/products/coverage";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { ReduxState } from "Store/rootReducer";
import { isDefined } from "Utils/functions";
import { Form } from "formik";
import React from "react";
import { useSelector } from "react-redux";
import { AkaFamilyAccidentProductIntroFormValues } from "../ProductIntro/akaFamilyAccidentProductIntroInterfaces";
import { setAkaFamilyAccidentCoverage } from "../akaFamilyAccidentUtils";
import { AkaFamilyAccidentCoverages } from "./AkaFamilyAccidentCoverages";
import { AkaFamilyAccidentProductPriceFormValues } from "./akaFamilyAccidentProductPriceInterfaces";
import { validationSchema } from "./akaFamilyAccidentProductPriceValidation";

const productName = INSURANCE_NAME_VALUE.AKA_FAMILY_ACCIDENT;

export const AkaFamilyAccidentProductPrice = ({
  updatePriceRequest,
}: CheckPriceProps): React.ReactElement => {
  const { getText } = useTexts();
  const handleAddProductToCart = useAddProductToCartHandler(
    AKA_FAMILY_ACCIDENT_PRICE_STEP.id,
  );

  const introFormValues = useFormState(
    AKA_FAMILY_ACCIDENT_ABOUT_INSURANCE_STEP.id,
  ) as Required<AkaFamilyAccidentProductIntroFormValues>;

  const { addProductToCart } = useSelector((state: ReduxState) => state);

  const handleSubmit = (
    formValues: AkaFamilyAccidentProductPriceFormValues,
  ): void => {
    const productToAdd = {
      coverage: setAkaFamilyAccidentCoverage(
        formValues,
      ) as AkaFamilyAccidentCoverage,
      productType: productName,
    };

    handleAddProductToCart(formValues, [productToAdd]);
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.AKA_FAMILY_ACCIDENT}`,
        );
      }}
    >
      <ProductPriceTile
        header={getText("akafamilyaccidentform.pageheader.checkprice")}
        initialValues={{
          ...introFormValues,
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <Form>
          <AkaRenewalInfoAlert
            productName={INSURANCE_NAME_VALUE.AKA_FAMILY_ACCIDENT}
          />
          <AkaFamilyAccidentCoverages updatePriceRequest={updatePriceRequest} />

          {isDefined(addProductToCart.error) && (
            <SystemErrorAlert alertId="addProductToCart" />
          )}

          <NavigationButtons isLoading={addProductToCart.isLoading} />
        </Form>
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
